import api from './api';

export interface ProRankingKeyFact {
  icon?: string | null;
  label?: string | null;
  value?: string | null;
  sublabel?: string | null;
  order?: number | null;
}

export interface ProRankingRecord {
  label?: string | null;
  value?: string | null;
  holder?: string | null;
  holderPlayerId?: string | null;
  year?: number | null;
  note?: string | null;
}

export interface ProRankingPastLeader {
  year?: number | null;
  playerName?: string | null;
  note?: string | null;
}

export interface ProRanking {
  _id?: string;
  id?: string;
  slug?: string | null;
  name?: string | null;
  shortName?: string | null;
  description?: string | null;
  organization?: string | null;
  image?: string | null;
  valueUnit?: string | null;
  valueLabel?: string | null;
  valueMultiplier?: number | null;
  valueMultiplierHint?: string | null;
  sortOrder?: number | null;
  active?: boolean;
  dataAsOf?: string | null;
  entryCount?: number | null;
  // Redaktionelle Info-Felder (KI-Sync) – alle nullable
  intro?: string | null;
  overview?: string | null;
  howItWorks?: string | null;
  keyFacts?: ProRankingKeyFact[] | null;
  records?: ProRankingRecord[] | null;
  pastLeaders?: ProRankingPastLeader[] | null;
  thumbnail?: string | null;
  heroImage?: string | null;
  sponsor?: string | null;
  officialWebsite?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string[] | null;
  /** SEO-Suchbegriffe (2026-09-20): manuell / KI, FAQs vom Sync – alle nullable */
  seoAliases?: string[] | null;
  seoAliasesAi?: string[] | null;
  faqs?: ProRankingFaq[] | null;
  /** Server-gerechnetes SEO-Paket (Title, Description, H1, Unterzeile, Intro-Satz, Aliase, FAQs) – null bei alten Backends */
  seo?: ProRankingSeo | null;
  /** Dach-Seiten, zu denen diese Rangliste gehört (z. B. ProTour OOM → Pro Tour), nur Detail – null bei alten Backends (2026-09-22) */
  hubSlugs?: string[] | null;
  hubs?: ProRankingHubRef[] | null;
  createdAt?: number | null;
  updatedAt?: number | null;
}

/** Kompakter Verweis auf eine Dach-Seite (/turniere/<slug>) */
export interface ProRankingHubRef {
  id?: string | null;
  slug: string;
  name?: string | null;
  shortName?: string | null;
  year?: number | null;
  hubFamily?: string | null;
}

export interface ProRankingFaq {
  question?: string | null;
  answer?: string | null;
  order?: number | null;
  anchor?: string | null;
}

export interface ProRankingSeo {
  title?: string | null;
  description?: string | null;
  h1?: string | null;
  akaLine?: string | null;
  introSentence?: string | null;
  aliases?: string[] | null;
  faqs?: ProRankingFaq[] | null;
}

export interface ProRankingEntry {
  _id?: string;
  id?: string;
  rankingId?: string | null;
  rank?: number | null;
  previousRank?: number | null;
  rankChange?: number | null;
  playerName?: string | null;
  externalPlayerId?: string | null;
  playerUrl?: string | null;
  playerImage?: string | null;
  flashlivePlayerId?: string | null;
  country?: string | null;
  value?: number | null;
  valueFormatted?: string | null;
  stats?: Record<string, unknown> | null;
  highlighted?: boolean;
  notes?: string | null;
  createdAt?: number | null;
  updatedAt?: number | null;
}

export interface ProRankingSummary {
  id?: string | null;
  name?: string | null;
  shortName?: string | null;
  organization?: string | null;
  valueUnit?: string | null;
  valueLabel?: string | null;
  valueMultiplier?: number | null;
  valueMultiplierHint?: string | null;
  dataAsOf?: string | null;
  image?: string | null;
}

export interface ProRankingPagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface ProRankingEntriesResponse {
  ranking: ProRankingSummary;
  entries: ProRankingEntry[];
  pagination: ProRankingPagination;
}

class ProRankingService {
  async getAllRankings(): Promise<ProRanking[]> {
    const res = await api.get('/api/pro-rankings');
    return Array.isArray(res.data) ? res.data : [];
  }

  async getRankingBySlug(slug: string): Promise<ProRanking | null> {
    try {
      const res = await api.get(`/api/pro-ranking/slug/${encodeURIComponent(slug)}`);
      return res.data || null;
    } catch (err) {
      return null;
    }
  }

  /** Alias-Slug („darts-weltrangliste“) → kanonischer Slug oder null (2026-09-20). */
  async resolveSlug(slug: string): Promise<string | null> {
    try {
      const res = await api.get(`/api/pro-ranking/resolve/${encodeURIComponent(slug)}`);
      const value = res.data?.slug;
      return typeof value === 'string' && value.trim() ? value : null;
    } catch (err) {
      return null;
    }
  }

  async getRankingEntries(
    rankingId: string,
    limit: number = 100,
    offset: number = 0,
  ): Promise<ProRankingEntriesResponse> {
    const res = await api.get(
      `/api/pro-ranking/${encodeURIComponent(rankingId)}/entries`,
      { params: { limit, offset } },
    );
    const data = res.data || {};
    return {
      ranking: data.ranking || {},
      entries: Array.isArray(data.entries) ? data.entries : [],
      pagination: data.pagination || { total: 0, limit, offset, hasMore: false },
    };
  }

  buildFileUrl(fileId?: string | null): string | null {
    if (!fileId) return null;
    if (fileId.startsWith('http://') || fileId.startsWith('https://')) return fileId;
    const base = api.defaults.baseURL || '';
    // Volle Pfade (z.B. "/api/file/…" vom KI-Banner) nicht doppelt prefixen
    if (fileId.startsWith('/')) return `${base}${fileId}`;
    return `${base}/api/file/${fileId}`;
  }

  formatValue(entry: ProRankingEntry, ranking?: ProRankingSummary | null): string {
    if (entry.valueFormatted && entry.valueFormatted.trim().length > 0) {
      return entry.valueFormatted;
    }
    if (entry.value == null || isNaN(entry.value)) return '-';
    const unit = ranking?.valueUnit || '';
    const formatted = entry.value.toLocaleString('de-DE');
    if (unit === '€' || unit === '£' || unit === '$') {
      return `${unit}${formatted}`;
    }
    if (unit) {
      return `${formatted} ${unit}`;
    }
    return formatted;
  }
}

export default new ProRankingService();
